import { createReducer } from '@reduxjs/toolkit';
import {
	ChatRef,
	CreditWallet,
	DEFAULT_USER_SETTINGS,
	DesignSystem,
	DesignSystemVersion,
	DropShadowMode,
	DsChat,
	Frame,
	OrganizationCreditWallet,
	Scene,
	SceneChat,
	SceneComment,
	DsMode,
	Subscription,
	Tier,
	User,
	UserSettings,
} from 'dragonmouse-shared/interfaces';
import { Style } from 'dragonmouse-shared/style';
import {
	LoadingState,
	optimisticallyUpdateDesignSystems,
	optimisticallyUpdateDsChats,
	optimisticallyUpdateFrames,
	optimisticallyUpdateSceneChats,
	optimisticallyUpdateScenes,
	optimisticallyUpdateUser,
	OrganizationWithRole,
	refreshFramePermissions,
	resolveOptimisticallyUpdatedDesignSystems,
	resolveOptimisticallyUpdatedDsChats,
	resolveOptimisticallyUpdatedFrames,
	resolveOptimisticallyUpdatedSceneChats,
	resolveOptimisticallyUpdatedScenes,
	resolveOptimisticallyUpdatedUser,
	clearActiveScene,
	setDesignSystemLoadError,
	setLoadingState,
	setSceneLoadError,
	updateActiveDesignSystemVersion,
	updateActiveDsChat,
	updateActiveSceneChat,
	updateActiveSceneDesignSystem,
	updateActiveSceneLatestDesignSystemVersion,
	updateActiveSceneLinkedDesignSystemVersion,
	updateActiveSceneDesignSystemModes,
	updateCreditWallet,
	updateDesignSystems,
	updateDsChatRefs,
	updateFrames,
	updateGuestFrames,
	updateGuestScenes,
	updateLatestPublishedVersionNumber,
	updateOrganizationCreditWallets,
	updateOrganizationDesignSystemIds,
	updateOrganizations,
	updateOrganizationSceneIds,
	updateSceneChatRefs,
	updateSceneComments,
	optimisticallyUpdateSceneComments,
	resolveOptimisticallyUpdatedSceneComments,
	updateScenes,
	updateSubscription,
	updateTrackedUsers,
	updateUser,
	updateUserDesignSystemIds,
	updateUserSceneIds,
} from './actions';
import { optimisticWriteIsResolvedByIds } from './util';

export type OptimisticWrite<T> = {
	id: string;
	operation: `insert` | `update` | `delete`;
	value: Partial<T> & { id: string };
};

// definition of our store's types
export type Store = {
	scenes: Scene[];
	guestScenes: Scene[];
	designSystems: DesignSystem[];
	frames: Frame[];
	guestFrames: Frame[];
	activeSceneChat: SceneChat | null;
	user: User | null;
	trackedUsers: User[];
	subscription: Subscription | null;
	creditWallet: CreditWallet | null;
	organizations: OrganizationWithRole[];
	organizationSceneIds: Record<string, string[]>;
	organizationDesignSystemIds: Record<string, string[]>;
	organizationCreditWallets: Record<string, OrganizationCreditWallet>;
	userSceneIds: string[];
	userDesignSystemIds: string[];
	activeDesignSystemVersion: DesignSystemVersion | null;
	latestPublishedVersionNumber: number | null;
	dsChatRefs: ChatRef[];
	activeDsChat: DsChat | null;
	sceneChatRefs: ChatRef[];
	sceneComments: SceneComment[];
	activeSceneDesignSystem: DesignSystem | null;
	activeSceneLinkedDesignSystemVersion: DesignSystemVersion | null;
	activeSceneLatestDesignSystemVersion: DesignSystemVersion | null;
	activeSceneDesignSystemModes: DsMode[] | null;
	optimisticSceneWrites: OptimisticWrite<Scene>[];
	optimisticDesignSystemWrites: OptimisticWrite<DesignSystem>[];
	optimisticFrameWrites: OptimisticWrite<Frame>[];
	optimisticSceneChatWrites: OptimisticWrite<SceneChat>[];
	optimisticSceneCommentWrites: OptimisticWrite<SceneComment>[];
	optimisticDsChatWrites: OptimisticWrite<DsChat>[];
	optimisticUserWrites: OptimisticWrite<User>[];
	loading: LoadingState;
	sceneLoadError: string | null;
	designSystemLoadError: string | null;
};

// initialize our slice of the store to its default value
const initialState: Store = {
	scenes: [],
	guestScenes: [],
	designSystems: [],
	frames: [],
	guestFrames: [],
	activeSceneChat: null,
	user: null,
	trackedUsers: [],
	subscription: null,
	creditWallet: null,
	organizations: [],
	organizationSceneIds: {},
	organizationDesignSystemIds: {},
	organizationCreditWallets: {},
	userSceneIds: [],
	userDesignSystemIds: [],
	activeDesignSystemVersion: null,
	latestPublishedVersionNumber: null,
	dsChatRefs: [],
	activeDsChat: null,
	sceneChatRefs: [],
	sceneComments: [],
	activeSceneDesignSystem: null,
	activeSceneLinkedDesignSystemVersion: null,
	activeSceneLatestDesignSystemVersion: null,
	activeSceneDesignSystemModes: null,
	optimisticSceneWrites: [],
	optimisticDesignSystemWrites: [],
	optimisticFrameWrites: [],
	optimisticSceneChatWrites: [],
	optimisticSceneCommentWrites: [],
	optimisticDsChatWrites: [],
	optimisticUserWrites: [],
	loading: {
		profile: 'never-loaded',
		scenes: 'never-loaded',
		activeDesignSystemVersion: 'never-loaded',
		activeDsChat: 'never-loaded',
		activeScene: {
			frames: 'never-loaded',
			chats: 'never-loaded',
		},
		guest: {
			scenes: 'never-loaded',
			frames: 'never-loaded',
		},
	},
	sceneLoadError: null,
	designSystemLoadError: null,
};

export const DEFAULT_CANVAS_COLOR: string = '#555555';
export const DEFAULT_CANVAS_DROP_SHADOW_MODE: DropShadowMode = DropShadowMode.MEDIUM;

function mergeOptimisticWrites<T>(
	existingWrites: OptimisticWrite<T>[],
	newWrites: OptimisticWrite<T>[]
): OptimisticWrite<T>[] {
	const newIds = newWrites.map((w) => w.id);
	return [
		// First we keep any optimistic writes that are not being added or updated.
		...existingWrites.filter((w) => !newIds.includes(w.id)),
		// Then we add the new optimistic writes. This moves both new and updated
		// writes to the end of the array which is what we want.
		...newWrites,
	];
}

const reducer = createReducer(initialState, (builder) => {
	//------------------------------------------------------------------------------------------------
	// scenes
	//------------------------------------------------------------------------------------------------

	builder.addCase(updateScenes, (state, action) => {
		state.scenes = action.payload;
	});

	builder.addCase(updateGuestScenes, (state, action) => {
		state.guestScenes = action.payload;
	});

	builder.addCase(optimisticallyUpdateScenes, (state, action) => {
		state.optimisticSceneWrites = mergeOptimisticWrites(
			state.optimisticSceneWrites,
			action.payload
		);
	});

	builder.addCase(resolveOptimisticallyUpdatedScenes, (state, action) => {
		state.optimisticSceneWrites = state.optimisticSceneWrites.filter(
			(write) => !optimisticWriteIsResolvedByIds(write, action.payload)
		);
	});

	builder.addCase(setSceneLoadError, (state, action) => {
		state.sceneLoadError = action.payload;
	});

	builder.addCase(setDesignSystemLoadError, (state, action) => {
		state.designSystemLoadError = action.payload;
	});

	//------------------------------------------------------------------------------------------------
	// design systems
	//------------------------------------------------------------------------------------------------

	builder.addCase(updateDesignSystems, (state, action) => {
		state.designSystems = action.payload;
	});

	builder.addCase(optimisticallyUpdateDesignSystems, (state, action) => {
		state.optimisticDesignSystemWrites = mergeOptimisticWrites(
			state.optimisticDesignSystemWrites,
			action.payload
		);
	});

	builder.addCase(resolveOptimisticallyUpdatedDesignSystems, (state, action) => {
		state.optimisticDesignSystemWrites = state.optimisticDesignSystemWrites.filter(
			(write) => !optimisticWriteIsResolvedByIds(write, action.payload)
		);
	});

	builder.addCase(updateActiveDesignSystemVersion, (state, action) => {
		state.activeDesignSystemVersion = action.payload;
	});

	builder.addCase(updateLatestPublishedVersionNumber, (state, action) => {
		state.latestPublishedVersionNumber = action.payload;
	});

	builder.addCase(updateDsChatRefs, (state, action) => {
		state.dsChatRefs = action.payload;
	});

	builder.addCase(updateActiveDsChat, (state, action) => {
		state.activeDsChat = action.payload;
	});

	builder.addCase(optimisticallyUpdateDsChats, (state, action) => {
		state.optimisticDsChatWrites = mergeOptimisticWrites(
			state.optimisticDsChatWrites,
			action.payload
		);
	});

	builder.addCase(resolveOptimisticallyUpdatedDsChats, (state, action) => {
		state.optimisticDsChatWrites = state.optimisticDsChatWrites.filter(
			(write) => !optimisticWriteIsResolvedByIds(write, action.payload)
		);
	});

	builder.addCase(clearActiveScene, (state) => {
		state.frames = [];
		state.activeSceneChat = null;
		state.sceneChatRefs = [];
		state.sceneComments = [];
		state.optimisticSceneCommentWrites = [];
		state.activeSceneDesignSystem = null;
		state.activeSceneDesignSystemModes = null;
		state.activeSceneLinkedDesignSystemVersion = null;
		state.activeSceneLatestDesignSystemVersion = null;
	});

	//------------------------------------------------------------------------------------------------
	// frames
	//------------------------------------------------------------------------------------------------

	builder.addCase(updateFrames, (state, action) => {
		state.frames = action.payload;
	});

	builder.addCase(refreshFramePermissions, (state) => {
		// Robustly update frame permissions based on current scenes collection
		// If a frame's scene is in the user's scenes collection, it's editable (viewOnly: false)
		// If a frame's scene is not in the collection, it's view-only (viewOnly: true)
		const userSceneIds = new Set(state.scenes.map((scene) => scene.id));
		state.frames = state.frames.map((frame) => ({
			...frame,
			viewOnly: !userSceneIds.has(frame.sceneId),
		}));
	});

	builder.addCase(updateGuestFrames, (state, action) => {
		state.guestFrames = action.payload;
	});

	builder.addCase(optimisticallyUpdateFrames, (state, action) => {
		state.optimisticFrameWrites = mergeOptimisticWrites(
			state.optimisticFrameWrites,
			action.payload
		);
	});

	builder.addCase(resolveOptimisticallyUpdatedFrames, (state, action) => {
		state.optimisticFrameWrites = state.optimisticFrameWrites.filter(
			(write) => !optimisticWriteIsResolvedByIds(write, action.payload)
		);
	});

	//------------------------------------------------------------------------------------------------
	// scene chats
	//------------------------------------------------------------------------------------------------

	builder.addCase(updateActiveSceneChat, (state, action) => {
		state.activeSceneChat = action.payload;
	});

	builder.addCase(optimisticallyUpdateSceneChats, (state, action) => {
		state.optimisticSceneChatWrites = mergeOptimisticWrites(
			state.optimisticSceneChatWrites,
			action.payload
		);
	});

	builder.addCase(resolveOptimisticallyUpdatedSceneChats, (state, action) => {
		state.optimisticSceneChatWrites = state.optimisticSceneChatWrites.filter(
			(write) => !optimisticWriteIsResolvedByIds(write, action.payload)
		);
	});

	builder.addCase(updateSceneChatRefs, (state, action) => {
		state.sceneChatRefs = action.payload;
	});

	builder.addCase(updateSceneComments, (state, action) => {
		state.sceneComments = action.payload;
	});

	builder.addCase(optimisticallyUpdateSceneComments, (state, action) => {
		state.optimisticSceneCommentWrites = mergeOptimisticWrites(
			state.optimisticSceneCommentWrites,
			action.payload
		);
	});

	builder.addCase(resolveOptimisticallyUpdatedSceneComments, (state, action) => {
		state.optimisticSceneCommentWrites = state.optimisticSceneCommentWrites.filter(
			(write) => !optimisticWriteIsResolvedByIds(write, action.payload)
		);
	});

	builder.addCase(updateActiveSceneDesignSystem, (state, action) => {
		state.activeSceneDesignSystem = action.payload;
	});

	builder.addCase(updateActiveSceneLinkedDesignSystemVersion, (state, action) => {
		state.activeSceneLinkedDesignSystemVersion = action.payload;
	});

	builder.addCase(updateActiveSceneLatestDesignSystemVersion, (state, action) => {
		state.activeSceneLatestDesignSystemVersion = action.payload;
	});

	builder.addCase(updateActiveSceneDesignSystemModes, (state, action) => {
		state.activeSceneDesignSystemModes = action.payload;
	});

	//------------------------------------------------------------------------------------------------
	// user
	//------------------------------------------------------------------------------------------------

	builder.addCase(updateUser, (state, action) => {
		state.user = action.payload;
	});

	builder.addCase(optimisticallyUpdateUser, (state, action) => {
		state.optimisticUserWrites = mergeOptimisticWrites(state.optimisticUserWrites, action.payload);
	});

	builder.addCase(resolveOptimisticallyUpdatedUser, (state, action) => {
		state.optimisticUserWrites = state.optimisticUserWrites.filter(
			(write) => !optimisticWriteIsResolvedByIds(write, action.payload)
		);
	});

	//------------------------------------------------------------------------------------------------
	// subscription
	//------------------------------------------------------------------------------------------------

	builder.addCase(updateSubscription, (state, action) => {
		state.subscription = action.payload;
	});

	//------------------------------------------------------------------------------------------------
	// credit wallet
	//------------------------------------------------------------------------------------------------

	builder.addCase(updateCreditWallet, (state, action) => {
		state.creditWallet = action.payload;
	});

	//------------------------------------------------------------------------------------------------
	// tracked users
	//------------------------------------------------------------------------------------------------

	builder.addCase(updateTrackedUsers, (state, action) => {
		state.trackedUsers = action.payload;
	});

	//------------------------------------------------------------------------------------------------
	// organizations
	//------------------------------------------------------------------------------------------------

	builder.addCase(updateOrganizations, (state, action) => {
		state.organizations = action.payload;
	});

	builder.addCase(updateOrganizationSceneIds, (state, action) => {
		state.organizationSceneIds = action.payload;
	});

	builder.addCase(updateOrganizationDesignSystemIds, (state, action) => {
		state.organizationDesignSystemIds = action.payload;
	});

	builder.addCase(updateOrganizationCreditWallets, (state, action) => {
		state.organizationCreditWallets = action.payload;
	});

	builder.addCase(updateUserSceneIds, (state, action) => {
		state.userSceneIds = action.payload;
	});

	builder.addCase(updateUserDesignSystemIds, (state, action) => {
		state.userDesignSystemIds = action.payload;
	});

	//------------------------------------------------------------------------------------------------
	// loading state
	//------------------------------------------------------------------------------------------------

	builder.addCase(setLoadingState, (state, action) => {
		const { resource, state: newState } = action.payload;

		// Handle nested paths like 'activeScene.frames'
		if (resource.includes('.')) {
			const [parent, child] = resource.split('.');
			if (parent === 'activeScene') {
				(state.loading.activeScene as any)[child] = newState;
			} else if (parent === 'guest') {
				(state.loading.guest as any)[child] = newState;
			}
		} else {
			// Handle top-level resources
			if (resource === 'profile') {
				state.loading.profile = newState;
			} else if (resource === 'scenes') {
				state.loading.scenes = newState;
			} else if (resource === 'activeDesignSystemVersion') {
				state.loading.activeDesignSystemVersion = newState;
			} else if (resource === 'activeDsChat') {
				state.loading.activeDsChat = newState;
			}
		}
	});
});

//------------------------------------------------------------------------------------------------
// selectors
//------------------------------------------------------------------------------------------------
export const selectActiveScene = (state: Store, activeSceneId: string | null): Scene | null => {
	if (!activeSceneId) return null;

	// Check regular scenes first (they take precedence over guest scenes)
	const scene = state.scenes.find((scene) => scene.id === activeSceneId) || null;
	if (scene) {
		// Apply optimistic writes to regular scenes
		const optimisticWrites = Object.values(state.optimisticSceneWrites);
		return optimisticWrites
			.filter((w) => w.value.id === activeSceneId)
			.reduce((scene: Scene | null, { operation, value }: OptimisticWrite<Scene>): Scene | null => {
				switch (operation) {
					case 'insert':
						return value as Scene;
					case 'update':
						return { ...scene, ...value } as Scene;
					case 'delete':
						return null;
					default:
						return scene;
				}
			}, scene);
	}

	// Fall back to guest scenes if not found in regular scenes
	// guest scenes are readonly so we don't need to check optimistic writes
	const guestScene = state.guestScenes.find((scene) => scene.id === activeSceneId) || null;
	return guestScene;
};

export const selectActiveStyle = (state: Store, activeSceneId: string | null): Style | null => {
	const scene = selectActiveScene(state, activeSceneId);
	if (!scene?.metadata?.styles?.length) return null;
	const currentStyle = scene.metadata?.currentStyle;
	if (currentStyle === null) return null;
	if (currentStyle >= scene.metadata.styles.length) return null;
	return scene.metadata.styles[currentStyle];
};

export const selectActiveSceneChat = (
	state: Store,
	expectedChatId?: string | null
): SceneChat | null => {
	const chatId = expectedChatId ?? state.activeSceneChat?.id ?? null;
	if (!chatId) return null;

	const sceneChat = state.activeSceneChat?.id === chatId ? state.activeSceneChat : null;
	const optimisticWrites = Object.values(state.optimisticSceneChatWrites);

	return optimisticWrites
		.filter((w) => w.value.id === chatId)
		.reduce(
			(
				chat: SceneChat | null,
				{ operation, value }: OptimisticWrite<SceneChat>
			): SceneChat | null => {
				switch (operation) {
					case 'insert':
						return value as SceneChat;
					case 'update':
						return { ...chat, ...value } as SceneChat;
					case 'delete':
						return null;
					default:
						return chat;
				}
			},
			sceneChat
		);
};

export const selectActiveDsChat = (state: Store, expectedChatId?: string | null): DsChat | null => {
	const chatId = expectedChatId ?? state.activeDsChat?.id ?? null;
	if (!chatId) return null;

	const dsChat = state.activeDsChat?.id === chatId ? state.activeDsChat : null;
	const optimisticWrites = Object.values(state.optimisticDsChatWrites);

	return optimisticWrites
		.filter((w) => w.value.id === chatId)
		.reduce((chat: DsChat | null, { operation, value }: OptimisticWrite<DsChat>): DsChat | null => {
			switch (operation) {
				case 'insert':
					return value as DsChat;
				case 'update':
					return { ...chat, ...value } as DsChat;
				case 'delete':
					return null;
				default:
					return chat;
			}
		}, dsChat);
};

export const selectScenes = (state: Store): Scene[] => {
	const optimisticWrites = Object.values(state.optimisticSceneWrites);

	const scenes = optimisticWrites.reduce(
		(scenes: Scene[], { operation, value }: OptimisticWrite<Scene>): Scene[] => {
			switch (operation) {
				case 'insert':
					return [...scenes, value as Scene];
				case 'update':
					return scenes.map((scene) => (scene.id === value.id ? { ...scene, ...value } : scene));
				case 'delete':
					return scenes.filter((scene) => scene.id !== value.id);
				default:
					return scenes;
			}
		},
		state.scenes
	);

	// Filter out guest scenes that already exist in regular scenes to avoid duplicates
	const regularSceneIds = new Set(scenes.map((scene) => scene.id));
	const uniqueGuestScenes = state.guestScenes.filter(
		(guestScene) => !regularSceneIds.has(guestScene.id)
	);

	return [...scenes, ...uniqueGuestScenes];
};

export const selectGuestScenes = (state: Store): Scene[] => {
	const optimisticWrites = Object.values(state.optimisticSceneWrites);

	const scenes = optimisticWrites.reduce(
		(scenes: Scene[], { operation, value }: OptimisticWrite<Scene>): Scene[] => {
			switch (operation) {
				case 'insert':
					return [...scenes, value as Scene];
				case 'update':
					return scenes.map((scene) => (scene.id === value.id ? { ...scene, ...value } : scene));
				case 'delete':
					return scenes.filter((scene) => scene.id !== value.id);
				default:
					return scenes;
			}
		},
		state.scenes
	);

	// Filter out guest scenes that already exist in regular scenes to avoid duplicates
	const regularSceneIds = new Set(scenes.map((scene) => scene.id));
	const uniqueGuestScenes = state.guestScenes.filter(
		(guestScene) => !regularSceneIds.has(guestScene.id)
	);

	return uniqueGuestScenes;
};

export const selectDesignSystems = (state: Store): DesignSystem[] => {
	const optimisticWrites = Object.values(state.optimisticDesignSystemWrites);

	const designSystems = optimisticWrites.reduce(
		(
			designSystems: DesignSystem[],
			{ operation, value }: OptimisticWrite<DesignSystem>
		): DesignSystem[] => {
			switch (operation) {
				case 'insert':
					return [...designSystems, value as DesignSystem];
				case 'update':
					return designSystems.map((ds) => (ds.id === value.id ? { ...ds, ...value } : ds));
				case 'delete':
					return designSystems.filter((ds) => ds.id !== value.id);
				default:
					return designSystems;
			}
		},
		state.designSystems
	);

	return designSystems;
};

export const selectFrames = (state: Store, includeDeleted: boolean = false): Frame[] => {
	const optimisticWrites = Object.values(state.optimisticFrameWrites);
	const frames = optimisticWrites.reduce(
		(frames: Frame[], { operation, value }: OptimisticWrite<Frame>): Frame[] => {
			switch (operation) {
				case 'insert':
					return [...frames, value as Frame];
				case 'update':
					return frames.map((frame) => (frame.id === value.id ? { ...frame, ...value } : frame));
				case 'delete':
					return frames.filter((frame) => includeDeleted || frame.id !== value.id);
				default:
					return frames;
			}
		},
		state.frames
	);

	if (!includeDeleted) {
		return frames.filter((frame) => !frame.deletedAt);
	}

	// add the guest frames to the list
	return [...frames, ...state.guestFrames];
};

export const selectFrame = (state: Store, frameId?: string): Frame | null => {
	if (!frameId) return null;

	const frame = state.frames.find((frame) => frame.id === frameId) ?? null;

	// XXX
	// XXX
	//
	// I've identified a conceptual bug with optimistic writes. I'm opting *not* to fix it now because:
	//  - we haven't noticed any issues
	// - I believe it's only when doing local development that this issue manifests.
	// - The solution I see involves a sizable amount of code
	// - I don't want to slow down to build the better solution now.
	// - I don't want to risk introducing an instability for no good reason
	//
	// However let me document what is going on
	// The issue is when state.optimisticFrameWrites contains multiple writes for the same frame.
	// THis was causing a bug when toggling the public/private toggle on frame sharing. On local dev,
	// these changes would stack up, and only the first change would be applied.
	// You can see below I updated the code here to use the last write, which fixes this particular bug.
	//
	// Two things concern me:
	//  - this is not a solid fix for the situation where the multiple writes affect different properties of
	//    the frame. they are not merged!
	//  - other methods here like selectFrames are not updated to use the last write.
	//    and they also don't merge the writes!
	//
	// I envision three possible solution directions
	//
	// PATH A
	// - make a function to merge updates from frames, chats, scenes, etc.
	// - here use that function to merge multiple writes for the same frame
	// - in the other methods, merge multiple writes for the same frame/chat/scene/etc and THEN do the reduce
	//
	// PATH B
	// - as optimistic writes get set, merge them when there's already a pending optimistic write for the same frame
	//
	// PATH C
	// - do something completely different.
	//
	// XXX
	// XXX

	// If there are multiple writes for this frame, use the last one (most recent)
	const optimisticWrites = Object.values(state.optimisticFrameWrites);
	const writes = optimisticWrites.filter((write) => write.value.id === frameId);
	if (writes.length > 0) {
		const write = writes[writes.length - 1];
		if (write.value.id === frameId) {
			switch (write.operation) {
				case 'insert':
					return write.value as Frame;
				case 'update':
					return { ...frame, ...write.value } as Frame;
				case 'delete':
					return null;
				default:
					return frame;
			}
		}
	}

	// If we found a frame in regular frames, return it
	if (frame) {
		return frame;
	}

	// If we didn't find it in the regular frames, check the guest frames
	const guestFrame = state.guestFrames.find((guestFrame) => guestFrame.id === frameId);
	if (guestFrame) {
		return guestFrame;
	}

	return null;
};

export const selectDsChatRefs = (state: Store): ChatRef[] => {
	return state.dsChatRefs;
};

export const selectSceneChatRefs = (state: Store): ChatRef[] => {
	return state.sceneChatRefs;
};

// Memoized: returns stable reference when inputs (sceneComments, optimisticSceneCommentWrites) unchanged
let _prevSceneComments: SceneComment[] = [];
let _prevOptimisticCommentWrites: OptimisticWrite<SceneComment>[] = [];
let _cachedSceneComments: SceneComment[] = [];

export const selectSceneComments = (state: Store): SceneComment[] => {
	if (
		state.sceneComments === _prevSceneComments &&
		state.optimisticSceneCommentWrites === _prevOptimisticCommentWrites
	) {
		return _cachedSceneComments;
	}
	_prevSceneComments = state.sceneComments;
	_prevOptimisticCommentWrites = state.optimisticSceneCommentWrites;

	const optimisticWrites = Object.values(state.optimisticSceneCommentWrites);
	const merged = optimisticWrites.reduce(
		(
			comments: SceneComment[],
			{ operation, value }: OptimisticWrite<SceneComment>
		): SceneComment[] => {
			switch (operation) {
				case 'insert':
					return [...comments, value as SceneComment];
				case 'update':
					return comments.map((c) => {
						if (c.id !== value.id) return c;
						const merged = { ...c, ...value };
						if (value.metadata && c.metadata) {
							merged.metadata = { ...c.metadata, ...value.metadata };
						}
						return merged;
					});
				case 'delete':
					return comments.filter((c) => c.id !== value.id);
				default:
					return comments;
			}
		},
		state.sceneComments
	);
	_cachedSceneComments = [...merged].sort((a, b) => {
		const aOrder = a.metadata?.sortOrder ?? '';
		const bOrder = b.metadata?.sortOrder ?? '';
		return aOrder < bOrder ? -1 : aOrder > bOrder ? 1 : 0;
	});
	return _cachedSceneComments;
};

export const selectUser = (state: Store): User | null => {
	const optimisticWrites = Object.values(state.optimisticUserWrites);

	if (optimisticWrites.length === 0) {
		return state.user;
	}

	return optimisticWrites.reduce(
		(user: User | null, { operation, value }: OptimisticWrite<User>): User | null => {
			switch (operation) {
				case 'insert':
					return value as User;
				case 'update':
					// If user is null, we can't update it, so return null
					// If user exists, merge the partial update
					return user ? ({ ...user, ...value } as User) : null;
				case 'delete':
					return null;
				default:
					return user;
			}
		},
		state.user ?? null
	);
};

export const selectTrackedUser = (state: Store, userId: string): User | null => {
	return state.trackedUsers.find((user) => user.id === userId) || null;
};

export const selectUserSettings = (state: Store): Required<UserSettings> => {
	const user = selectUser(state);

	// Guest users or no user: return defaults
	if (!user || user.id === 'guest') {
		return DEFAULT_USER_SETTINGS;
	}

	// Merge account settings with defaults
	return {
		...DEFAULT_USER_SETTINGS,
		...user.metadata?.settings,
	};
};

export const selectCreditWallet = (state: Store): CreditWallet | null => {
	return state.creditWallet;
};

export const selectIsGuest = (state: Store): boolean => {
	const user = selectUser(state);
	return user?.id === 'guest';
};

export const selectIsFreeTier = (state: Store): boolean => {
	return (state.subscription?.tier ?? Tier.FREE) === Tier.FREE;
};

export const selectOrganizations = (state: Store): OrganizationWithRole[] => {
	return state.organizations;
};

export const selectOrganizationSceneIds = (state: Store): Record<string, string[]> => {
	return state.organizationSceneIds;
};

export const selectOrganizationDesignSystemIds = (state: Store): Record<string, string[]> => {
	return state.organizationDesignSystemIds;
};

export const selectOrganizationCreditWallets = (
	state: Store
): Record<string, OrganizationCreditWallet> => {
	return state.organizationCreditWallets;
};

export const selectUserSceneIds = (state: Store): string[] => {
	return state.userSceneIds;
};

export const selectUserDesignSystemIds = (state: Store): string[] => {
	return state.userDesignSystemIds;
};

export const selectActiveDesignSystem = (
	state: Store,
	activeDesignSystemId: string | null
): DesignSystem | null => {
	if (!activeDesignSystemId) return null;

	const designSystem = state.designSystems.find((ds) => ds.id === activeDesignSystemId) || null;
	const optimisticWrites = Object.values(state.optimisticDesignSystemWrites);

	return optimisticWrites
		.filter((w) => w.value.id === activeDesignSystemId)
		.reduce(
			(
				ds: DesignSystem | null,
				{ operation, value }: OptimisticWrite<DesignSystem>
			): DesignSystem | null => {
				switch (operation) {
					case 'insert':
						return value as DesignSystem;
					case 'update':
						return { ...ds, ...value } as DesignSystem;
					case 'delete':
						return null;
					default:
						return ds;
				}
			},
			designSystem
		);
};

export const selectActiveDesignSystemVersion = (state: Store): DesignSystemVersion | null => {
	return state.activeDesignSystemVersion;
};

export const selectActiveDesignSystemIsPreparing = (state: Store): boolean => {
	const versionNumber = state.activeDesignSystemVersion?.versionNumber ?? null;
	const isUnpublished = versionNumber === 0 && state.latestPublishedVersionNumber === null;
	const promptGenerationStatus = state.activeDesignSystemVersion?.manifest?.promptGenerationStatus;
	return (
		isUnpublished && promptGenerationStatus !== 'complete' && promptGenerationStatus !== 'failed'
	);
};

export const selectActiveSceneDesignSystem = (
	state: Store,
	_activeSceneId: string | null
): DesignSystem | null => {
	return state.activeSceneDesignSystem;
};

export const selectActiveSceneLinkedDesignSystemVersion = (
	state: Store
): DesignSystemVersion | null => {
	return state.activeSceneLinkedDesignSystemVersion;
};

export const selectActiveSceneLatestDesignSystemVersion = (
	state: Store
): DesignSystemVersion | null => {
	return state.activeSceneLatestDesignSystemVersion;
};

export default reducer;
